import { useState, useEffect, useRef, useCallback } from "react";

/* ─────────────────────────────────────────────────────────
   INJECT FONTS + KEYFRAMES
───────────────────────────────────────────────────────── */
let _injected = false;
if (typeof document !== "undefined" && !_injected) {
  _injected = true;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&family=Outfit:wght@300;400;500;600&display=swap";
  document.head.appendChild(link);
  const s = document.createElement("style");
  s.innerHTML = `
    @keyframes pat-pulse   { 0%,100%{opacity:1} 50%{opacity:.15} }
    @keyframes pat-heart   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.12)} }
    @keyframes pat-kenburn { 0%{transform:scale(1)} 100%{transform:scale(1.06)} }
    @keyframes pat-fadeup  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
    @keyframes pat-prog    { from{width:0%} to{width:100%} }
    .pat-kb { animation: pat-kenburn 4s ease-out forwards; }
    @media(max-width:640px){
      .pat-feat-card { flex-direction:column !important; }
      .pat-feat-left { width:100% !important; min-height:110px !important; border-radius:12px 12px 0 0 !important; }
      .pat-sg        { grid-template-columns:repeat(2,1fr) !important; }
      .pat-glass-bar { flex-wrap:wrap !important; }
      .pat-glass-bar > div { width:50% !important; border-right:none !important; border-bottom:1px solid rgba(255,255,255,.08) !important; }
    }
  `;
  document.head.appendChild(s);
}

/* ─────────────────────────────────────────────────────────
   TOKENS
───────────────────────────────────────────────────────── */
const C = {
  r3:"#CC0000", r4:"#E63333", r5:"#FF6666",
  rg:"linear-gradient(135deg,#7B0000,#CC0000)",
  w:"#FFFFFF", off:"#FFF8F8", border:"#F0D8D8",
  text:"#1A0000", sub:"#5C2020", light:"#B07070",
  fd:"'Barlow Condensed',sans-serif",
  fu:"'Outfit',sans-serif",
};

/* ─────────────────────────────────────────────────────────
   CAROUSEL DATA  — 3s per slide
───────────────────────────────────────────────────────── */
const SLIDES = [
  { img:"https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=1400&q=80", tag:"Tournament 2024"    },
  { img:"https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=1400&q=80", tag:"Training Sessions"  },
  { img:"https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=1400&q=80", tag:"Championship Events" },
  { img:"https://images.unsplash.com/photo-1529408632839-a54952c491e5?w=1400&q=80", tag:"Award Ceremony"     },
];
const DURATION = 2000; // ms

/* ─────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────── */
const FEATURED = [
  { name:"Rajesh Patel",  role:"Business Owner",     since:"January 2022", seed:"Rajesh", bg:"b6e3f4",
    msg:"Supporting ADBA has been one of the most rewarding decisions. Watching young talent from Anand grow into state-level champions is truly priceless." },
  { name:"Meera Shah",    role:"Parent & Advocate",  since:"March 2022",   seed:"Meera",  bg:"ffdfbf",
    msg:"Badminton completely changed my son's life. ADBA gives every child a real shot at greatness and I'm proud to be part of that journey." },
  { name:"Arjun Desai",   role:"Former State Player", since:"June 2023",   seed:"Arjun",  bg:"c0aede",
    msg:"The dedication of ADBA coaches is unmatched. As a former player I know what great coaching means. Proud to support this community." },
];

const SUPPORTERS = [
  { name:"Priya Mehta",   since:"Jan 2023", seed:"Priya",   bg:"b6e3f4" },
  { name:"Kiran Joshi",   since:"Feb 2023", seed:"Kiran",   bg:"fef3c7" },
  { name:"Sneha Rao",     since:"Mar 2023", seed:"Sneha",   bg:"d1fae5" },
  { name:"Vishal Nair",   since:"Apr 2023", seed:"Vishal",  bg:"fecaca" },
  { name:"Anita Kumar",   since:"May 2023", seed:"Anita",   bg:"ede9fe" },
  { name:"Dev Sharma",    since:"Jun 2022", seed:"Dev",     bg:"ffdfbf" },
  { name:"Pooja Verma",   since:"Jul 2023", seed:"Pooja",   bg:"b6e3f4" },
  { name:"Ravi Gupta",    since:"Aug 2023", seed:"Ravi",    bg:"fef3c7" },
  { name:"Nisha Patel",   since:"Sep 2023", seed:"Nisha",   bg:"d1fae5" },
  { name:"Amit Thakur",   since:"Oct 2023", seed:"Amit",    bg:"fecaca" },
  { name:"Kavita Singh",  since:"Nov 2023", seed:"Kavita",  bg:"ede9fe" },
  { name:"Suresh Pillai", since:"Dec 2023", seed:"Suresh",  bg:"ffdfbf" },
  { name:"Deepa Menon",   since:"Jan 2024", seed:"Deepa",   bg:"b6e3f4" },
  { name:"Harish Reddy",  since:"Feb 2024", seed:"Harish",  bg:"fef3c7" },
  { name:"Lakshmi Iyer",  since:"Mar 2024", seed:"Lakshmi", bg:"d1fae5" },
  { name:"Rohit Agarwal", since:"Apr 2024", seed:"Rohit",   bg:"fecaca" },
];

/* ─────────────────────────────────────────────────────────
   REVEAL HOOK
───────────────────────────────────────────────────────── */
const useReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold, rootMargin:"0px 0px -28px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
};

/* ─────────────────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────────────────── */
const SHead = ({ words, sub }) => (
  <div style={{ marginBottom:36 }}>
    <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:10 }}>
      <div style={{ width:4, height:34, background:C.rg, borderRadius:2, flexShrink:0 }}/>
      <h2 style={{
        fontFamily:C.fd, fontSize:"clamp(1.5rem,3vw,2.4rem)",
        fontWeight:800, letterSpacing:1, textTransform:"uppercase",
        color:C.text, margin:0, lineHeight:1,
      }}>
        {words.map((w,i)=>(
          <span key={i} style={{ color:i===words.length-1?C.r3:C.text }}>{w}{" "}</span>
        ))}
      </h2>
    </div>
    {sub && <p style={{
      fontFamily:C.fu, fontSize:"0.87rem", color:C.sub,
      lineHeight:1.72, maxWidth:430, marginLeft:18,
      fontWeight:400, marginTop:0,
    }}>{sub}</p>}
  </div>
);

/* ─────────────────────────────────────────────────────────
   FEATURED CARD
───────────────────────────────────────────────────────── */
const FeatCard = ({ f, delay, idx }) => {
  const [ref, vis] = useReveal();
  const [hov, setHov] = useState(false);
  return (
    <div ref={ref}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      className="pat-feat-card"
      style={{
        display:"flex",
        background:C.w,
        border:`1px solid ${hov?C.r4:C.border}`,
        borderRadius:14, overflow:"hidden",
        boxShadow:hov?"0 0 0 1px rgba(230,51,51,.1),0 14px 44px rgba(26,0,0,.1)":"0 2px 8px rgba(26,0,0,.05)",
        opacity:vis?1:0, transform:vis?"translateY(0)":"translateY(24px)",
        transition:`opacity .6s ${delay}s ease,transform .6s ${delay}s ease,border-color .25s,box-shadow .25s`,
        cursor:"default",
      }}
    >
      <div className="pat-feat-left" style={{
        width:185, flexShrink:0,
        background:idx%2===1?"linear-gradient(160deg,#2a0000,#550000)":C.rg,
        display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center",
        padding:"26px 14px", gap:10,
        position:"relative", overflow:"hidden",
      }}>
        <div style={{ position:"absolute",width:200,height:200,borderRadius:"50%",border:"1px solid rgba(255,255,255,.07)",top:"50%",left:"50%",transform:"translate(-50%,-50%)" }}/>
        <div style={{ position:"absolute",width:130,height:130,borderRadius:"50%",border:"1px solid rgba(255,255,255,.1)",top:"50%",left:"50%",transform:"translate(-50%,-50%)" }}/>
        <img src={`https://api.dicebear.com/9.x/notionists/svg?seed=${f.seed}&backgroundColor=${f.bg}`}
          alt={f.name} style={{ width:66,height:66,borderRadius:"50%",border:"2.5px solid rgba(255,255,255,.35)",objectFit:"cover",position:"relative",zIndex:1,boxShadow:"0 6px 18px rgba(0,0,0,.25)" }}/>
        <div style={{ position:"relative",zIndex:1,textAlign:"center" }}>
          <div style={{ fontFamily:C.fd,fontSize:"1.05rem",fontWeight:800,textTransform:"uppercase",color:C.w,letterSpacing:.5,lineHeight:1 }}>{f.name}</div>
          <div style={{ fontFamily:C.fu,fontSize:"0.58rem",fontWeight:500,color:"rgba(255,255,255,.5)",letterSpacing:"1px",textTransform:"uppercase",marginTop:4 }}>{f.role}</div>
        </div>
      </div>
      <div style={{ flex:1,padding:"26px 24px",display:"flex",flexDirection:"column",justifyContent:"space-between" }}>
        <div>
          <div style={{ fontFamily:C.fd,fontSize:"3rem",lineHeight:.5,color:C.r3,opacity:.18,marginBottom:10,userSelect:"none" }}>"</div>
          <p style={{ fontFamily:C.fu,fontSize:"0.92rem",color:C.sub,lineHeight:1.78,fontWeight:300,margin:0,fontStyle:"italic" }}>{f.msg}</p>
        </div>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:18,paddingTop:13,borderTop:`1px solid ${C.border}` }}>
          <span style={{ display:"flex",alignItems:"center",gap:7,fontFamily:C.fu,fontSize:"0.62rem",fontWeight:600,letterSpacing:"1.5px",textTransform:"uppercase",color:C.light }}>
            <span style={{ width:5,height:5,borderRadius:"50%",background:C.rg,flexShrink:0 }}/>
            Supporter since {f.since}
          </span>
          <span style={{ fontFamily:C.fu,fontSize:"0.6rem",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",color:C.r3,opacity:.6 }}>ADBA Patron</span>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   SUPPORTER TILE
───────────────────────────────────────────────────────── */
const STile = ({ s, delay }) => {
  const [ref, vis] = useReveal();
  const [hov, setHov] = useState(false);
  return (
    <div ref={ref}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        background:C.w, border:`1px solid ${hov?C.r4:C.border}`,
        borderRadius:10, padding:"16px 14px",
        display:"flex", flexDirection:"column", alignItems:"flex-start",
        position:"relative", overflow:"hidden",
        boxShadow:hov?"0 0 0 1px rgba(230,51,51,.08),0 8px 22px rgba(26,0,0,.08)":"0 1px 3px rgba(26,0,0,.04)",
        opacity:vis?1:0, transform:vis?"translateY(0)":"translateY(16px)",
        transition:`opacity .45s ${delay}s ease,transform .45s ${delay}s ease,border-color .2s,box-shadow .2s`,
        cursor:"default",
      }}
    >
      <div style={{ position:"absolute",top:0,left:0,bottom:0,width:3,background:C.rg,opacity:hov?1:0,transition:"opacity .2s" }}/>
      <img src={`https://api.dicebear.com/9.x/notionists/svg?seed=${s.seed}&backgroundColor=${s.bg}`}
        alt={s.name} style={{ width:40,height:40,borderRadius:"50%",border:`1.5px solid ${hov?C.r4:C.border}`,objectFit:"cover",marginBottom:9,display:"block",transition:"border-color .2s" }}/>
      <div style={{ fontFamily:C.fd,fontSize:"1rem",fontWeight:800,textTransform:"uppercase",letterSpacing:.2,color:C.text,marginBottom:2 }}>{s.name}</div>
      <div style={{ fontFamily:C.fu,fontSize:"0.58rem",fontWeight:600,letterSpacing:"1.5px",textTransform:"uppercase",color:C.light,marginTop:"auto",paddingTop:7 }}>Since {s.since}</div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   PAGE
───────────────────────────────────────────────────────── */
const PatreonPage = () => {
  const [cur,     setCur]     = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [progKey, setProgKey] = useState(0);
  const [paused,  setPaused]  = useState(false);
  const timerRef = useRef(null);

  const [featRef, featVis] = useReveal(0.06);
  const [allRef,  allVis]  = useReveal(0.05);
  const [apprRef, apprVis] = useReveal(0.1);

  const goTo = useCallback((idx) => {
    setCur(idx);
    setAnimKey(k => k + 1);
    setProgKey(k => k + 1);
  }, []);

  const next = useCallback(() => goTo((cur + 1) % SLIDES.length), [cur, goTo]);
  const prev = useCallback(() => goTo((cur - 1 + SLIDES.length) % SLIDES.length), [cur, goTo]);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(next, DURATION);
    return () => clearTimeout(timerRef.current);
  }, [cur, paused, next]);

  return (
    <div style={{ background:C.off, fontFamily:C.fu, minHeight:"100vh" }}>

      {/* ══════════════════════════════════════
          HERO  — compact, single image carousel
      ══════════════════════════════════════ */}
      <section
        style={{
          position:"relative",
          height:"65vh",
          minHeight:460,
          maxHeight:640,
          overflow:"hidden",
        }}
        onMouseEnter={()=>setPaused(true)}
        onMouseLeave={()=>setPaused(false)}
      >
        {/* ── SLIDES ── */}
        {SLIDES.map((sl, i) => (
          <div key={i} style={{
            position:"absolute", inset:0,
            opacity: i === cur ? 1 : 0,
            transition:"opacity .75s ease",
            zIndex: i === cur ? 1 : 0,
          }}>
            <img
              key={i === cur ? animKey : i}
              src={sl.img}
              alt={sl.tag}
              className={i === cur ? "pat-kb" : ""}
              style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", transformOrigin:"center center" }}
            />
          </div>
        ))}

        {/* ── OVERLAYS ── */}
        <div style={{ position:"absolute",inset:0,zIndex:2, background:"linear-gradient(to bottom,rgba(0,0,0,.3) 0%,rgba(0,0,0,.15) 35%,rgba(10,0,0,.72) 75%,rgba(18,0,0,.94) 100%)" }}/>
        <div style={{ position:"absolute",inset:0,zIndex:3, background:"rgba(80,0,0,.15)", mixBlendMode:"multiply" }}/>
        <div style={{ position:"absolute",bottom:-40,left:-40,zIndex:4, width:420,height:340,borderRadius:"50%", background:"radial-gradient(ellipse,rgba(100,0,0,.35) 0%,transparent 65%)", pointerEvents:"none" }}/>

        {/* ── CONTENT ── */}
        <div style={{
          position:"absolute", inset:0, zIndex:5,
          display:"flex", flexDirection:"column",
          justifyContent:"flex-end",
          padding:"0 5% 0",
        }}>

          {/* slide tag */}
          <div key={`tag-${animKey}`} style={{
            display:"inline-flex", alignItems:"center", gap:7,
            fontFamily:C.fu, fontSize:"0.62rem", fontWeight:600,
            letterSpacing:"2.5px", textTransform:"uppercase",
            color:C.r5,
            border:"1px solid rgba(255,102,102,.22)",
            background:"rgba(255,102,102,.07)",
            backdropFilter:"blur(8px)",
            padding:"4px 14px", borderRadius:100,
            marginBottom:14, alignSelf:"flex-start",
            animation:"pat-fadeup .5s ease forwards",
          }}>
            <span style={{ width:4,height:4,borderRadius:"50%",background:C.r5,display:"inline-block",animation:"pat-pulse 2s infinite" }}/>
            {SLIDES[cur].tag}
          </div>

          {/* ── MAIN HEADING + SUBTITLE ── */}
          <div key={`text-${animKey}`} style={{ animation:"pat-fadeup .55s .05s ease both", marginBottom:20 }}>
            <h1 style={{
              fontFamily:C.fd,
              fontSize:"clamp(3rem,7.5vw,6.5rem)",
              fontWeight:800, lineHeight:.88,
              letterSpacing:2, textTransform:"uppercase",
              margin:"0 0 14px",
            }}>
              <span style={{ color:C.w, display:"block" }}>OUR PATREON</span>
              <span style={{
                display:"block",
                background:C.rg,
                WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text",
              }}>SUPPORTERS</span>
            </h1>
            <p style={{
              fontFamily:C.fu, fontSize:"clamp(.82rem,1.4vw,.97rem)",
              color:"rgba(255,255,255,.55)",
              fontWeight:300, lineHeight:1.7,
              margin:0, maxWidth:460,
            }}>
              The incredible people who believe in our mission and help us build
              the future of badminton in Anand district.
            </p>
          </div>

          {/* ── GLASS STATS BAR ── */}
          <div className="pat-glass-bar" style={{
            display:"flex",
            background:"rgba(255,255,255,.07)",
            backdropFilter:"blur(20px)",
            WebkitBackdropFilter:"blur(20px)",
            border:"1px solid rgba(255,255,255,.11)",
            borderRadius:12, overflow:"hidden",
            marginBottom:24, maxWidth:520,
          }}>
            {[
              { n:"120", s:"+", l:"Supporters"   },
              { n:"3",   s:"",  l:"Years Active"  },
              { n:"12",  s:"+", l:"Events Funded" },
              { n:"100", s:"%", l:"Community Led" },
            ].map((st,i,arr)=>(
              <div key={i} style={{
                flex:1, padding:"14px 8px",
                borderRight:i<arr.length-1?"1px solid rgba(255,255,255,.08)":"none",
                textAlign:"center",
              }}>
                <span style={{ display:"block",fontFamily:C.fd,fontSize:"1.85rem",fontWeight:800,color:C.w,lineHeight:1,letterSpacing:-1 }}>
                  {st.n}<em style={{ color:C.r5,fontStyle:"normal" }}>{st.s}</em>
                </span>
                <span style={{ display:"block",fontFamily:C.fu,fontSize:"0.54rem",fontWeight:700,letterSpacing:"1.8px",textTransform:"uppercase",color:"rgba(255,255,255,.42)",marginTop:3 }}>{st.l}</span>
              </div>
            ))}
          </div>

          {/* ── BOTTOM ROW: CTAs + controls ── */}
          <div style={{
            display:"flex", alignItems:"center",
            justifyContent:"space-between",
            flexWrap:"wrap", gap:14,
            paddingBottom:28,
          }}>
            {/* CTAs */}
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <a href="https://www.patreon.com" target="_blank" rel="noopener noreferrer"
                style={{
                  display:"inline-flex", alignItems:"center", gap:8,
                  fontFamily:C.fu, fontWeight:600, fontSize:"0.86rem",
                  padding:"11px 24px", borderRadius:6,
                  background:C.rg, color:C.w,
                  textDecoration:"none",
                  boxShadow:"0 4px 16px rgba(100,0,0,.4)",
                }}
              >
                Support on Patreon
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </a>
              <a href="#featured"
                style={{
                  display:"inline-flex", alignItems:"center",
                  fontFamily:C.fu, fontWeight:500, fontSize:"0.84rem",
                  padding:"11px 20px", borderRadius:6,
                  background:"rgba(255,255,255,.09)",
                  backdropFilter:"blur(8px)",
                  color:"rgba(255,255,255,.75)",
                  border:"1px solid rgba(255,255,255,.14)",
                  textDecoration:"none",
                }}
              >Meet the Community</a>
            </div>

            {/* Carousel controls */}
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              {/* arrows */}
              {[{ fn:prev, ch:"‹" }, { fn:next, ch:"›" }].map(({ fn, ch }, i) => (
                <button key={i} onClick={fn}
                  style={{
                    width:36, height:36, borderRadius:"50%",
                    background:"rgba(255,255,255,.1)",
                    backdropFilter:"blur(6px)",
                    border:"1px solid rgba(255,255,255,.18)",
                    color:C.w, fontSize:"1rem", cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    transition:"background .2s",
                    padding:0,
                  }}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(180,0,0,.55)"}
                  onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,.1)"}
                >{ch}</button>
              ))}

              {/* progress dots */}
              <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                {SLIDES.map((_,i)=>(
                  <button key={i} onClick={()=>goTo(i)}
                    style={{
                      position:"relative",
                      width:i===cur?36:7, height:4, borderRadius:100,
                      background:i===cur?"transparent":"rgba(255,255,255,.22)",
                      border:"none", cursor:"pointer", padding:0,
                      overflow:"hidden",
                      transition:"width .3s ease",
                      flexShrink:0,
                    }}
                  >
                    {i===cur && (
                      <>
                        <div style={{ position:"absolute",inset:0,background:"rgba(255,255,255,.22)",borderRadius:100 }}/>
                        <div key={progKey} style={{
                          position:"absolute",top:0,left:0,bottom:0,
                          background:C.rg, borderRadius:100,
                          animation:`pat-prog ${DURATION}ms linear forwards`,
                          animationPlayState:paused?"paused":"running",
                        }}/>
                      </>
                    )}
                  </button>
                ))}
              </div>

              {/* counter */}
              <span style={{ fontFamily:C.fd,fontSize:"0.95rem",fontWeight:700,color:"rgba(255,255,255,.45)",letterSpacing:1 }}>
                <span style={{ color:C.w }}>{String(cur+1).padStart(2,"0")}</span>/{String(SLIDES.length).padStart(2,"0")}
              </span>
            </div>
          </div>
        </div>

        {/* vertical label */}
        <div style={{
          position:"absolute", right:22, top:"50%",
          transform:"translateY(-50%) rotate(90deg)",
          zIndex:6,
          fontFamily:C.fu, fontSize:"0.55rem", fontWeight:700,
          letterSpacing:"3px", textTransform:"uppercase",
          color:"rgba(255,255,255,.22)", whiteSpace:"nowrap",
        }}>Anand District Badminton Association</div>
      </section>

      {/* ══════════════════════════════════════
          FEATURED SUPPORTERS
      ══════════════════════════════════════ */}
      <section style={{ background:C.w, padding:"68px 5% 60px", borderTop:`1px solid ${C.border}` }} id="featured">
        <div style={{ maxWidth:1060,margin:"0 auto" }}>
          <div ref={featRef} style={{ opacity:featVis?1:0,transform:featVis?"none":"translateY(18px)",transition:"opacity .6s ease,transform .6s ease" }}>
            <SHead words={["Featured","Supporters"]} sub="Our most dedicated patrons — the champions behind the champions."/>
          </div>
          <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
            {FEATURED.map((f,i)=><FeatCard key={f.name} f={f} delay={i*0.1} idx={i}/>)}
          </div>
        </div>
      </section>

      <div style={{ height:1,background:C.border,maxWidth:1060,margin:"0 auto" }}/>

      {/* ══════════════════════════════════════
          ALL SUPPORTERS
      ══════════════════════════════════════ */}
      <section style={{ background:C.off, padding:"60px 5% 68px" }}>
        <div style={{ maxWidth:1060,margin:"0 auto" }}>
          <div ref={allRef} style={{ opacity:allVis?1:0,transform:allVis?"none":"translateY(18px)",transition:"opacity .6s ease,transform .6s ease" }}>
            <SHead words={["All","Supporters"]} sub="Every name here is part of something that matters. Thank you."/>
          </div>
          <div className="pat-sg" style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(165px,1fr))",gap:10 }}>
            {SUPPORTERS.map((s,i)=><STile key={s.name} s={s} delay={Math.min(i*0.04,.26)}/>)}
          </div>
          <div style={{ marginTop:24,paddingTop:16,borderTop:`1px solid ${C.border}`,textAlign:"center",fontFamily:C.fu,fontSize:"0.76rem",color:C.light }}>
            Showing <strong style={{color:C.text}}>{SUPPORTERS.length}</strong> supporters · ADBA Patreon
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          APPRECIATION  — light theme
      ══════════════════════════════════════ */}
      <section style={{
        background:C.w,
        borderTop:`1px solid ${C.border}`,
        padding:"80px 5%",
        textAlign:"center",
        position:"relative",
        overflow:"hidden",
      }}>
        {/* very soft red glow top center */}
        <div style={{ position:"absolute",top:-140,left:"50%",transform:"translateX(-50%)",width:640,height:320,borderRadius:"50%",background:"radial-gradient(ellipse,rgba(204,0,0,.05) 0%,transparent 70%)",pointerEvents:"none" }}/>
        {/* dot pattern */}
        <div style={{ position:"absolute",inset:0,pointerEvents:"none",
          backgroundImage:"radial-gradient(circle,rgba(204,0,0,.04) 1px,transparent 1px)",
          backgroundSize:"30px 30px" }}/>
        {/* top red accent bar */}
        <div style={{ position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:72,height:3,background:C.rg,borderRadius:"0 0 4px 4px" }}/>

        <div ref={apprRef} style={{
          position:"relative",zIndex:1,
          maxWidth:580,margin:"0 auto",
          opacity:apprVis?1:0,
          transform:apprVis?"none":"translateY(24px)",
          transition:"opacity .75s ease,transform .75s ease",
        }}>
          <span style={{ fontSize:"2.2rem",display:"block",marginBottom:16,animation:"pat-heart 2.2s ease-in-out infinite" }}>🏸</span>

          <div style={{
            display:"inline-flex",alignItems:"center",gap:7,
            fontFamily:C.fu,fontSize:"0.6rem",fontWeight:600,
            letterSpacing:"2.5px",textTransform:"uppercase",
            color:C.r3,border:`1px solid rgba(204,0,0,.2)`,
            background:"rgba(204,0,0,.05)",
            padding:"4px 14px",borderRadius:100,marginBottom:18,
          }}>A note of gratitude</div>

          <h2 style={{
            fontFamily:C.fd,fontWeight:800,textTransform:"uppercase",
            fontSize:"clamp(1.9rem,4.5vw,3.5rem)",
            letterSpacing:1,lineHeight:.95,color:C.text,marginBottom:16,
          }}>
            POWERED BY YOUR<br/>
            <span style={{ background:C.rg,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text" }}>
              BELIEF IN US.
            </span>
          </h2>

          <p style={{ fontFamily:C.fu,fontSize:"0.93rem",color:C.sub,lineHeight:1.85,fontWeight:300,marginBottom:28,maxWidth:460,margin:"0 auto 28px" }}>
            We are incredibly grateful to every supporter who helps make our work possible.
            Because of you, the next generation of Anand's badminton players has a home,
            a court, and a community that believes in them.
          </p>

          <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:12 }}>
            <div style={{ height:1,width:36,background:C.border }}/>
            <span style={{ fontFamily:C.fd,fontSize:"1rem",fontWeight:700,letterSpacing:.5,color:C.light,textTransform:"uppercase" }}>
              With gratitude,&nbsp;
              <span style={{ background:C.rg,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text" }}>ADBA TEAM ✦</span>
            </span>
            <div style={{ height:1,width:36,background:C.border }}/>
          </div>
        </div>
      </section>

    </div>
  );
};

export default PatreonPage;